import { IconButton, Link, Tooltip } from '@material-ui/core';
import { ArrowForward, FileCopyOutlined } from '@material-ui/icons';
import {
  ColorDynamic,
  Column,
  Columns,
  Image,
  Inline,
  Stack,
  SuperDispatchTheme,
  useResponsiveValue,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { round } from 'es-toolkit';
import { useEffect, useRef } from 'react';
import { useClipboard } from 'shared/helpers/DomHelpers';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import {
  convertPricePerKmToPricePerMile,
  kmToMile,
} from 'shared/utils/MeasurementUtils';
import styled, { css } from 'styled-components';
import { trackCarrierPayEvent } from '../../../data/CarrierPayAnalytics';
import { PricingInsightsResultDTO } from '../../../data/CarrierPayDTO';
import { ShippingDetailsDTO } from '../../../data/ShippingDetailsDTO';
import { Blurred } from '../unlock/Blurred';
import { ContactSalesForm } from '../unlock/ContactSalesForm';
import { ConfidenceScore } from './ConfidenceScore';
import emptyRecommendationImg from './assets/empty_recommendation.png';
import emptyRecommendationImgDark from './assets/empty_recommendation_dark.png';
import { PricingRecommendationTabs } from './PricingRecommendationTabs';
import { UsageProgress } from './UsageProgress';
import { DemoBanner } from './DemoBanner';
import { LocationsIcon } from '../../../icons/LocationsIcon';
import { makeCityStateZip } from 'shared/helpers/AddressHelpers';

const Content = styled.div<{ $disableDesktopScroll?: boolean }>`
  flex: 1;
  padding: 24px;
  width: 720px;
  max-width: 720px;

  background-color: ${ColorDynamic.White};
  border-radius: 3px;
  border: 1px solid ${ColorDynamic.Silver400};

  @media (max-width: 760px) {
    width: 100%;
  }

  @media (min-width: 760px) {
    min-height: max(100vh - 112px, 800px);
    height: ${({ $disableDesktopScroll }) =>
      $disableDesktopScroll ? 'auto' : 'max(100vh - 112px, 800px)'};
    overflow: ${({ $disableDesktopScroll }) =>
      $disableDesktopScroll ? 'visible' : 'auto'};
  }

  @media (min-width: 1440px) {
    max-width: 912px;
  }
`;

const EmptyBannerContainer = styled.div(
  ({ theme }: { theme: SuperDispatchTheme }) => css`
    display: flex;
    justify-content: center;

    ${theme.breakpoints.up('sm')} {
      margin-top: 32px;
    }
  `,
);

const PricePerMileLink = styled(Link)`
  cursor: default;
  border-bottom: 1px ${ColorDynamic.Silver500} dashed;
`;

function EmptyContent() {
  return (
    <Box marginTop={['small', 'xxlarge']} paddingTop={['none', 'large']}>
      <Stack align="center">
        <Image
          src={emptyRecommendationImg}
          srcDark={emptyRecommendationImgDark}
          alt=""
          width="258"
        />
        <TextBox color="secondary" variant="heading-3" align="center">
          Start with your Shipping Details
        </TextBox>

        <TextBox color="secondary" align="center">
          Enter route and vehicle details <br /> to get your Pricing
          Recommendation.
        </TextBox>
      </Stack>
    </Box>
  );
}

interface PricingRecommendationProps {
  isLocked: boolean;
  shippingDetails: ShippingDetailsDTO | null;
  pricingInsights: PricingInsightsResultDTO | null;
  showDemoBanner?: boolean;
}

export function PricingRecommendation({
  isLocked,
  pricingInsights,
  shippingDetails,
  showDemoBanner = false,
}: PricingRecommendationProps) {
  const ref = useRef<HTMLElement | null>(null);
  const { copy, status } = useClipboard();
  const { addSnackbar } = useSnackbarStack();
  const isMobile = useResponsiveValue(true, false);
  const carrierPay = pricingInsights?.carrier_pay;

  useEffect(() => {
    if (status === 'copied') {
      addSnackbar('Price Copied.');
    }
  }, [status, addSnackbar]);

  useEffect(() => {
    if (pricingInsights && isMobile) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [pricingInsights, ref, isMobile]);

  return (
    <Content $disableDesktopScroll={isLocked}>
      <Stack space="small">
        <TextBox ref={ref} id="pricing-recommendation" variant="heading-2">
          Pricing Recommendation
        </TextBox>
        <UsageProgress />
        {showDemoBanner && <DemoBanner />}
        {!pricingInsights ? (
          isLocked ? (
            <EmptyBannerContainer>
              <ContactSalesForm />
            </EmptyBannerContainer>
          ) : (
            <EmptyContent />
          )
        ) : (
          <Stack space="medium">
            <Columns space="xsmall">
              <Column width="content">
                <LocationsIcon fontSize="small" color="action" />
              </Column>

              <Column>
                <Inline space="xsmall" verticalAlign="center">
                  {shippingDetails?.origin && (
                    <TextBox color="secondary">
                      {makeCityStateZip(shippingDetails.origin)}
                    </TextBox>
                  )}

                  <ArrowForward fontSize="small" color="action" />

                  {shippingDetails?.destination && (
                    <TextBox color="secondary">
                      {makeCityStateZip(shippingDetails.destination)}
                    </TextBox>
                  )}

                  {!!carrierPay?.distance && (
                    <TextBox color="secondary">
                      {' '}
                      · {kmToMile(carrierPay.distance, 2)} mi
                    </TextBox>
                  )}
                </Inline>
              </Column>
            </Columns>

            {carrierPay ? (
              <Stack space="none">
                <TextBox>Estimated Carrier Price</TextBox>
                <Blurred $disabled={!isLocked}>
                  <Columns
                    space="medium"
                    align="bottom"
                    collapseBelow="desktop"
                  >
                    <Column width={['fluid', 'fluid', 'content']}>
                      <Columns space="small" align="bottom">
                        <Column width={['fluid', 'fluid', 'content']}>
                          <Inline space="xxsmall" verticalAlign="center">
                            <TextBox variant="heading-1">
                              {formatCurrency(round(carrierPay.total_price))}
                            </TextBox>

                            <IconButton
                              size="small"
                              style={{ fontSize: 16 }}
                              onClick={() => {
                                copy(String(round(carrierPay.total_price)));
                                if (shippingDetails) {
                                  trackCarrierPayEvent({
                                    name: 'Copied Price from Pricing Insights',
                                    carrierPay,
                                    shippingDetails,
                                  });
                                }
                              }}
                            >
                              <FileCopyOutlined
                                fontSize="inherit"
                                color="action"
                              />
                            </IconButton>
                          </Inline>
                        </Column>

                        <Column width="content">
                          <Tooltip title="Price per Vehicle">
                            <TextBox color="secondary">
                              <PricePerMileLink
                                href="#"
                                color="inherit"
                                underline="none"
                              >
                                {formatCurrency(
                                  convertPricePerKmToPricePerMile(
                                    carrierPay.price_per_km /
                                      (shippingDetails?.vehicles?.length || 1),
                                    2,
                                  ),
                                  {
                                    minimumFractionDigits: 2,
                                  },
                                )}
                                /mi
                              </PricePerMileLink>{' '}
                              · {kmToMile(carrierPay.distance, 2)} mi
                            </TextBox>
                          </Tooltip>
                        </Column>
                      </Columns>
                    </Column>

                    <Column width={['fluid', 'fluid', 'content']}>
                      <ConfidenceScore confidence={carrierPay.confidence} />
                    </Column>
                  </Columns>
                </Blurred>
              </Stack>
            ) : (
              <Stack space="none">
                <TextBox>Estimated Carrier Price</TextBox>
                <TextBox variant="heading-2">N/A</TextBox>
              </Stack>
            )}

            <PricingRecommendationTabs
              pricingInsights={pricingInsights}
              isLocked={isLocked}
            />
          </Stack>
        )}
      </Stack>
    </Content>
  );
}
